// ─── Il circolo descritto dall'applicativo: campi, fasce, chiusure, maestri ──
//
// 🎯 È il pezzo che rende il gestionale AUTONOMO. Oggi campi e disponibilità
// arrivano da Matchpoint: il giorno in cui si spegne, un applicativo che non sa
// da sé quali campi ha e a che ora si gioca non è un applicativo. Per questo
// sta prima del calendario e non dopo.
import { z } from 'zod';
import { emailSchema } from './auth.js';
/**
 * `HH:MM`. Postgres rende un `time` come `HH:MM:SS`: la normalizzazione sta
 * nella query (`to_char`), non qui — uno schema che accetta due forme è uno
 * schema che lascia passare due forme fino allo schermo.
 */
export const oraSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Serve un orario nella forma 14:30');
/** `YYYY-MM-DD`, la data senza fuso: un listino vale per giorni di calendario. */
export const dataSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Serve una data nella forma 2026-09-15');
/**
 * 0 = domenica … 6 = sabato, come `extract(dow)` di Postgres — e come
 * `Date#getDay()` di JavaScript. Le due convenzioni coincidono: è una fortuna,
 * e si dichiara qui invece di lasciarla scoprire a chi sposta un indice.
 */
export const giornoSchema = z.number().int().min(0).max(6);
export const GIORNI = ['Domenica', 'Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato'];
/**
 * L'ordine in cui i giorni si MOSTRANO: da lunedì a domenica, come li conta chi
 * lavora — richiesta sua del 16/09.
 *
 * 🚨 È una lista di INDICI, e non un secondo elenco di nomi, per una ragione
 * che non è di stile: in `GIORNI` **l'indice è il dato**. Lo 0 è domenica
 * perché lo è per `extract(dow)` di Postgres e per `Date#getDay()`, e quel
 * numero sta scritto nella colonna `weekday` di ogni fascia già salvata.
 * ⛔ Riordinare `GIORNI` per farlo cominciare dal lunedì sposterebbe TUTTI i
 *    significati di un posto: le fasce del lunedì diventerebbero di domenica
 *    nella lettura, in silenzio e su tutti i dati esistenti.
 * 📌 L'ordine di lettura è una scelta di chi guarda; l'ordine dei numeri è un
 *    fatto del database. Si tengono separati, o il primo corrompe il secondo.
 */
export const ORDINE_SETTIMANA = [1, 2, 3, 4, 5, 6, 0];
/** Il giorno della settimana di `2026-09-15`, senza passare da un fuso orario. */
export function giornoDellaData(data) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(data);
    if (!m)
        return null;
    const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
    if (Number.isNaN(d.getTime()))
        return null;
    return d.getUTCDay();
}
// ── I campi ─────────────────────────────────────────────────────────────────
export const campoSchema = z.object({
    id: z.uuid(),
    name: z.string(),
    sortOrder: z.number().int(),
    surface: z.string().nullable(),
    indoor: z.boolean().nullable(),
    /** Un campo spento sparisce dalla griglia; le sue prenotazioni passate restano. */
    active: z.boolean(),
});
export const elencoCampiSchema = z.object({ items: z.array(campoSchema) });
export const creaCampoSchema = z.object({
    name: z.string().trim().min(1, 'Serve il nome del campo').max(60),
    sortOrder: z.number().int().min(0).max(999),
    surface: z.string().trim().max(60).nullable().default(null),
    indoor: z.boolean().nullable().default(null),
});
/**
 * 🚨 SCRITTO PER ESTESO, e NON come `creaCampoSchema.partial()`.
 *
 * 📏 Provato invece che dedotto: `.partial()` lascia in piedi i `.default(null)`
 * della creazione, quindi `{ name: 'X' }` esce da Zod come
 * `{ name: 'X', surface: null, indoor: null }`. ⇒ Una modifica del solo nome
 * **cancellava in silenzio** superficie e «coperto», e la guardia della rotta
 * (`'surface' in m`) non poteva accorgersene: la chiave c'era davvero.
 * 📌 *Uno schema di modifica non è uno schema di creazione con tutto opzionale:
 *    lì «assente» vuol dire «metti il valore normale», qui «non toccarlo».*
 */
export const modificaCampoSchema = z
    .object({
    name: z.string().trim().min(1, 'Serve il nome del campo').max(60).optional(),
    sortOrder: z.number().int().min(0).max(999).optional(),
    surface: z.string().trim().max(60).nullable().optional(),
    indoor: z.boolean().nullable().optional(),
    active: z.boolean().optional(),
})
    .refine((v) => Object.keys(v).length > 0, { message: 'Non c’è niente da cambiare' });
// ── L'orario del circolo ────────────────────────────────────────────────────
//
// ⭐⭐ NON È UNA FASCIA, ED È LA DISTINZIONE CHE TIENE IN PIEDI TUTTO IL RESTO.
//
// · l'ORARIO DEL CIRCOLO dice **quando il circolo è aperto**: è ciò che il
//   calendario disegna, ed è dentro di lui che la segreteria può prenotare;
// · una FASCIA dice **quando possono prenotare i soci**, ed è una porzione di
//   quell'orario.
//
// ⚖️ Fino al 16/09/2026 la seconda faceva anche il mestiere della prima, e non
// per disegno: il calendario non aveva nessun altro posto da cui sapere da che
// ora a che ora disegnare, quindi guardava gli estremi delle fasce. ⇒ Le ore
// senza fascia **non esistevano affatto** — non si vedevano, e il server le
// rifiutava a chiunque, segreteria compresa. 🗣️ Richiesta sua: *«nel calendario
// devo vedere le fasce non prenotabili, perché queste non sono prenotabili per
// i soci, ma noi di segreteria possiamo prenotarle»*.
// 📌 *Una regola che vale per tutti perché nessuno ha mai scritto per chi vale
//    non è una regola: è una mancanza che si è indurita.*
/**
 * Come `oraSchema`, ma arriva a `24:00`.
 *
 * 🚨 `24:00` NON è `00:00`, e la differenza non è di stile: una chiusura scritta
 * `00:00` è **prima** dell'apertura, quindi il circolo risulterebbe aperto per
 * un tempo negativo e la griglia lunga zero minuti. Postgres è d'accordo —
 * `'24:00:00'::time` è un valore legittimo, ed è l'unico sopra le 23:59 — e
 * `oraDaMinuti(1440)` risponde `24:00` apposta (⇒ `calendario.ts`).
 */
export const oraChiusuraSchema = z
    .string()
    .regex(/^(([01]\d|2[0-3]):[0-5]\d|24:00)$/, 'Serve un orario nella forma 23:30, oppure 24:00');
/**
 * L'orario di apertura, uno solo e uguale tutti i giorni.
 *
 * ⚖️ **Uno solo, e non uno per giorno della settimana**: una griglia che cambia
 * forma dal lunedì al martedì non si legge a colpo d'occhio, e chi lavora al
 * calendario lo guarda cento volte al giorno. Che la domenica si giochi meno lo
 * dicono le **fasce**, che sono già per giorno: qui si dichiara il contenitore,
 * non il contenuto.
 */
export const orarioCircoloSchema = z.object({
    opensAt: oraSchema,
    closesAt: oraChiusuraSchema,
});
/**
 * 08:00 → 24:00, deciso da lui il 16/09/2026.
 *
 * ⚠️ Vive QUI e non solo nel `default` della colonna, perché serve anche al
 * browser quando la risposta non è ancora arrivata: una griglia che aspetta di
 * sapere l'orario per disegnarsi lampeggerebbe a ogni cambio di giorno.
 */
export const ORARIO_CIRCOLO_PREDEFINITO = { opensAt: '08:00', closesAt: '24:00' };
/**
 * 🚨 Tutt'e due le ore, sempre: qui «assente» non ha un significato utile.
 *
 * ⚖️ È l'opposto di `modificaCampoSchema`, e la differenza è nel dato: un campo
 * ha tanti attributi indipendenti, e cambiarne uno non deve toccare gli altri.
 * L'orario è **una cosa sola in due metà**, e le due si controllano a vicenda
 * (`closesAt > opensAt`): accettarne una per volta vorrebbe dire accettare uno
 * stato che il controllo non sa più giudicare.
 */
export const modificaOrarioCircoloSchema = orarioCircoloSchema.refine((v) => v.closesAt > v.opensAt, {
    message: 'Il circolo deve chiudere dopo che ha aperto',
    path: ['closesAt'],
});
// ── Le fasce prenotabili ────────────────────────────────────────────────────
export const fasciaSchema = z.object({
    id: z.uuid(),
    /** Nullo = tutti i campi. È il caso normale del circolo: un orario solo. */
    courtId: z.uuid().nullable(),
    weekday: giornoSchema,
    startsAtTime: oraSchema,
    endsAtTime: oraSchema,
    /** Il passo della griglia: 90 al Padel Village, ed è un dato, non una costante. */
    slotMinutes: z.number().int(),
    validFrom: dataSchema.nullable(),
    validTo: dataSchema.nullable(),
    active: z.boolean(),
});
export const elencoFasceSchema = z.object({ items: z.array(fasciaSchema) });
export const creaFasciaSchema = z
    .object({
    courtId: z.uuid().nullable().default(null),
    weekday: giornoSchema,
    startsAtTime: oraSchema,
    endsAtTime: oraSchema,
    slotMinutes: z.number().int().min(5).max(240),
    validFrom: dataSchema.nullable().default(null),
    validTo: dataSchema.nullable().default(null),
})
    .refine((v) => v.endsAtTime > v.startsAtTime, {
    message: 'La fascia deve finire dopo che è cominciata',
    path: ['endsAtTime'],
})
    .refine((v) => v.validTo === null || v.validFrom === null || v.validTo >= v.validFrom, {
    message: 'Il periodo di validità finisce prima di cominciare',
    path: ['validTo'],
});
export const modificaFasciaSchema = z
    .object({
    /**
     * ⚠️ `null` qui vuol dire «tutti i campi», non «non toccarlo» — ed è per
     * questo che la rotta guarda `'courtId' in m` invece del valore: assente e
     * nullo sono due gesti diversi, e confonderli riporterebbe a tutti i campi
     * una fascia che qualcuno aveva assegnato a uno solo.
     */
    courtId: z.uuid().nullable().optional(),
    /** Spostare una fascia di giorno è una modifica come le altre: il vincolo di
        non sovrapposizione è già per giorno, quindi il database regge da solo. */
    weekday: giornoSchema.optional(),
    startsAtTime: oraSchema.optional(),
    endsAtTime: oraSchema.optional(),
    slotMinutes: z.number().int().min(5).max(240).optional(),
    validFrom: dataSchema.nullable().optional(),
    validTo: dataSchema.nullable().optional(),
    active: z.boolean().optional(),
})
    .refine((v) => Object.keys(v).length > 0, { message: 'Non c’è niente da cambiare' })
    /**
     * ⚙️ Il confronto si fa solo quando arrivano TUTT'E DUE le ore. Con una sola,
     * l'altra la sa il database e non questo schema: arrotondarla qui vorrebbe dire
     * confrontare un'ora nuova con una che nessuno ha letto. Il caso «ne cambio una
     * sola e la metto alla rovescia» lo prende `bookable_slots_ore_check`, che l'ora
     * vecchia ce l'ha davvero.
     */
    .refine((v) => v.startsAtTime === undefined || v.endsAtTime === undefined || v.endsAtTime > v.startsAtTime, {
    message: 'La fascia deve finire dopo che è cominciata',
    path: ['endsAtTime'],
})
    .refine((v) => v.validFrom === undefined ||
    v.validTo === undefined ||
    v.validTo === null ||
    v.validFrom === null ||
    v.validTo >= v.validFrom, { message: 'Il periodo di validità finisce prima di cominciare', path: ['validTo'] });
/**
 * ⭐⭐ LA GRIGLIA DELLE FASCE: un giorno intero, riscritto in un colpo.
 *
 * 🗣️ Nasce dalla sua domanda del 18/09/2026 — *«è possibile replicare la stessa
 * modalità di inserimento che abbiamo fatto per i listini?»* — e dalla
 * differenza che quella domanda porta con sé: nel listino una casella è una
 * riga, qui N caselle sono UNA riga. ⇒ Non si può scrivere «questa casella»:
 * si dice **com’è fatto il giorno adesso**, e il server ci arriva.
 *
 * ⛔ L’ambito viaggia INSIEME ai giorni e non dentro ogni banda: un foglio
 * appartiene a un campo (o a tutti) e a un periodo, e mescolare due ambiti nella
 * stessa scrittura vorrebbe dire due verità per la stessa casella.
 *
 * ⚠️ `active: false` non è una banda da buttare: è una banda che **dorme col suo
 * passo**, ed è la ragione per cui lui ha voluto le caselle spente.
 */
export const bandaDisegnataSchema = z
    .object({
    startsAtTime: oraSchema,
    endsAtTime: oraSchema,
    slotMinutes: z.number().int().min(5).max(240),
    active: z.boolean().default(true),
})
    .refine((v) => v.endsAtTime > v.startsAtTime, {
    message: 'La fascia deve finire dopo che è cominciata',
    path: ['endsAtTime'],
});
export const scriviGrigliaFasceSchema = z.object({
    courtId: z.uuid().nullable().default(null),
    validFrom: dataSchema.nullable().default(null),
    validTo: dataSchema.nullable().default(null),
    /** Solo i giorni TOCCATI: quelli che non arrivano non si guardano nemmeno. */
    giorni: z
        .array(z.object({ weekday: giornoSchema, bande: z.array(bandaDisegnataSchema).max(48) }))
        .min(1)
        .max(7),
});
// ── Le chiusure ─────────────────────────────────────────────────────────────
export const chiusuraSchema = z.object({
    id: z.uuid(),
    courtId: z.uuid().nullable(),
    startsAt: z.string(),
    endsAt: z.string(),
    reason: z.string().nullable(),
    /**
     * Festività fissa: contano mese e giorno, l'anno no. ⚠️ Una ricorrente non
     * può scavalcare il capodanno — per il 28/12 → 02/01 si fanno due righe.
     */
    everyYear: z.boolean(),
    createdBy: z.uuid().nullable(),
});
export const elencoChiusureSchema = z.object({ items: z.array(chiusuraSchema) });
export const creaChiusuraSchema = z
    .object({
    courtId: z.uuid().nullable().default(null),
    startsAt: z.iso.datetime({ offset: true }),
    endsAt: z.iso.datetime({ offset: true }),
    reason: z.string().trim().max(200).nullable().default(null),
    everyYear: z.boolean().default(false),
})
    .refine((v) => v.endsAt > v.startsAt, {
    message: 'La chiusura deve finire dopo che è cominciata',
    path: ['endsAt'],
})
    .refine((v) => !v.everyYear || v.startsAt.slice(0, 4) === v.endsAt.slice(0, 4), {
    // Il confronto mese-giorno di una ricorrente che scavalca l'anno direbbe
    // «aperto» in silenzio. Si vieta qui e lo rifiuta anche il database.
    message: 'Una chiusura di ogni anno non può scavalcare il capodanno: servono due righe',
    path: ['everyYear'],
});
/**
 * Cambiare una chiusura già scritta.
 *
 * 🗣️ Richiesta del committente del 17/09/2026: *«manca il modifica su questa
 * vista»*. Correggere una data sbagliata voleva dire cancellare e riscrivere —
 * e chi cancella prima di riscrivere, se poi sbaglia il modulo, resta con un
 * circolo aperto a Natale e nessuna traccia di cosa c'era scritto.
 *
 * ⚠️ Vale la regola di `modificaFasciaSchema`: ciò che NON arriva non si tocca,
 * e per `courtId` il nullo è un VALORE («tutti i campi»), non un'assenza.
 */
export const modificaChiusuraSchema = z
    .object({
    courtId: z.uuid().nullable().optional(),
    startsAt: z.iso.datetime({ offset: true }).optional(),
    endsAt: z.iso.datetime({ offset: true }).optional(),
    reason: z.string().trim().max(200).nullable().optional(),
    everyYear: z.boolean().optional(),
})
    .refine((v) => Object.keys(v).length > 0, { message: 'Non c’è niente da cambiare' })
    /**
     * ⚙️ Come per le fasce, il confronto si fa solo con TUTT'E DUE le date. Con
     * una sola, l'altra la sa il database e non questo schema, e a prendere il
     * caso storto resta `court_closures_ordine_check`, che quella vecchia ce
     * l'ha davvero.
     */
    .refine((v) => v.startsAt === undefined || v.endsAt === undefined || v.endsAt > v.startsAt, {
    message: 'La chiusura deve finire dopo che è cominciata',
    path: ['endsAt'],
})
    /**
     * 🚨 Stessa prudenza sul capodanno: si controlla solo quando arrivano insieme
     * il «ogni anno» e le due date. Negli altri casi il vincolo del database ha
     * in mano i valori veri, questo schema no — e un controllo fatto su metà dei
     * dati direbbe «va bene» proprio quando non va bene.
     */
    .refine((v) => v.everyYear !== true ||
    v.startsAt === undefined ||
    v.endsAt === undefined ||
    v.startsAt.slice(0, 4) === v.endsAt.slice(0, 4), {
    message: 'Una chiusura di ogni anno non può scavalcare il capodanno: servono due righe',
    path: ['everyYear'],
});
/**
 * ⭐⭐ **I DUE GIORNI DI UNA CHIUSURA, IN UN POSTO SOLO.**
 *
 * 🚨 La domanda «quali giorni chiude questa riga?» si rispondeva in **due
 * posti con due convenzioni diverse**, e una delle due sbagliava di un giorno.
 * Qui c'è una definizione sola, e la usano tutti.
 *
 * Le due trappole, che sono il motivo per cui una `slice(0, 10)` non basta:
 *
 * ① **`ends_at` È UN CONFINE, e il confine sta fuori.** Una chiusura dal 25 al
 *    27 è scritta con la fine alla **mezzanotte del 28**, perché il 27 dev'essere
 *    chiuso per intero. ⇒ L'ultimo giorno chiuso è quello che contiene
 *    `ends_at` **meno un istante** — ed è la stessa aritmetica del database
 *    (`(e.e - interval '1 microsecond')::date`, in `chiusureDelPeriodo`).
 * ② **Il testo ISO non porta il giorno del circolo.** Il server manda
 *    `2026-12-24T23:00:00.000Z`, che in Italia è il **25** dicembre: leggere i
 *    primi dieci caratteri dà il giorno di **Greenwich**, cioè uno in meno per
 *    metà dell'anno. ⇒ Si passa da `Date`, che il giorno lo calcola nel fuso di
 *    chi esegue.
 *
 * ⚠️ E quel fuso è un'ASSUNZIONE dichiarata: è quello del browser della
 * segreteria, che è quello del circolo. Chi eseguisse queste righe da un altro
 * fuso otterrebbe i giorni di casa sua.
 */
export function giornoDellIstante(istante) {
    const d = istante instanceof Date ? istante : new Date(istante);
    const due = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${due(d.getMonth() + 1)}-${due(d.getDate())}`;
}
/** Il PRIMO giorno chiuso. */
export function primoGiornoChiuso(c) {
    return giornoDellIstante(c.startsAt);
}
/** L'ULTIMO giorno chiuso, che NON è il giorno di `endsAt` — vedi ① qui sopra. */
export function ultimoGiornoChiuso(c) {
    return giornoDellIstante(new Date(new Date(c.endsAt).getTime() - 1));
}
// ── I maestri ───────────────────────────────────────────────────────────────
export const maestroSchema = z.object({
    id: z.uuid(),
    displayName: z.string(),
    /** Se il maestro è anche socio è LA STESSA PERSONA, non una seconda anagrafica. */
    memberId: z.uuid().nullable(),
    sortOrder: z.number().int(),
    active: z.boolean(),
});
/**
 * 🎾⭐ **L'ACCESSO DEL MAESTRO, e sta in uno schema A PARTE di proposito.**
 *
 * ⚠️ `maestroSchema` non viaggia solo qui: viaggia dentro `GET /calendar`
 * (⇒ `calendario.ts`), che legge chiunque abbia `view_dashboard`. Attaccandoci
 * l'email dell'accesso, l'indirizzo di ogni maestro finirebbe in ogni
 * caricamento del calendario di ogni operatore — compresi quelli che non hanno
 * `view_admin_utenti`, cioè proprio quelli a cui gli accessi non si mostrano.
 * 📌 *Uno schema riusato è un canale riusato: ciò che gli si aggiunge esce da
 *    tutte le porte da cui passava già, non solo da quella che si aveva in mente.*
 */
export const accessoDelMaestroSchema = z.object({
    staffUserId: z.uuid(),
    email: z.string(),
    /** Se l'ha già scelta lui. Finché è `false`, l'accesso esiste ma non entra. */
    hasPassword: z.boolean(),
    /** L'utente è attivo. Spento, la porta è chiusa anche se il maestro è in servizio. */
    active: z.boolean(),
});
/** Il maestro come lo vede la SEGRETERIA: con lo stato del suo accesso. */
export const maestroDiSegreteriaSchema = maestroSchema.extend({
    accesso: accessoDelMaestroSchema.nullable(),
});
export const elencoMaestriSchema = z.object({ items: z.array(maestroDiSegreteriaSchema) });
/**
 * Aprire l'accesso a un maestro: si dà un'email e basta.
 *
 * ⛔ **Nessuno sceglie una password per qualcun altro**, qui come in
 * `/settings/users`: l'accesso nasce **senza**, e il maestro se la mette da sé
 * con `/auth/register`. Una password decisa da terzi è una password che viaggia
 * in un messaggio.
 */
export const apriAccessoMaestroSchema = z.object({ email: emailSchema });
export const creaMaestroSchema = z.object({
    displayName: z.string().trim().min(1, 'Serve il nome del maestro').max(120),
    memberId: z.uuid().nullable().default(null),
    sortOrder: z.number().int().min(0).max(999).default(0),
});
export const modificaMaestroSchema = z
    .object({
    displayName: z.string().trim().min(1).max(120).optional(),
    memberId: z.uuid().nullable().optional(),
    sortOrder: z.number().int().min(0).max(999).optional(),
    active: z.boolean().optional(),
})
    .refine((v) => Object.keys(v).length > 0, { message: 'Non c’è niente da cambiare' });
// ── Il perimetro di un maestro: dove il circolo gli ha aperto ────────────────
//
// 🗣️ *«maestro per maestro, sulla sua scheda»* (18/09/2026).
//
// ⚖️ I nomi sono quelli di `fasciaSchema` apposta — `courtId` nullo = tutti i
// campi, `weekday` con la convenzione di Postgres — ma **la regola è l'opposta**:
// le fasce del circolo si SOSTITUISCONO, le bande di un maestro si SOMMANO.
// Il perché sta in `migrations/1758340000000_perimetro_dei_maestri.cjs` e la
// prova che lo dichiara in `perimetro-maestro.test.ts`.
export const bandaMaestroSchema = z.object({
    id: z.uuid(),
    /** Nullo = tutti i campi: il maestro che gira su quello che si libera. */
    courtId: z.uuid().nullable(),
    weekday: giornoSchema,
    startsAtTime: oraSchema,
    endsAtTime: oraSchema,
    active: z.boolean(),
});
export const elencoBandeMaestroSchema = z.object({ items: z.array(bandaMaestroSchema) });
export const creaBandaMaestroSchema = z
    .object({
    courtId: z.uuid().nullable().default(null),
    weekday: giornoSchema,
    startsAtTime: oraSchema,
    endsAtTime: oraSchema,
})
    .refine((v) => v.endsAtTime > v.startsAtTime, {
    message: 'La banda deve finire dopo che è cominciata',
    path: ['endsAtTime'],
});
/*
 * 🔄 **LA TARIFFA UNICA NON C'È PIÙ** (voce 19, tappa 19a, 24/09/2026): qui
 * stavano gli schemi di `court_rates`, una cifra sola all'ora per tutto il
 * circolo. Il campo al maestro adesso è un **listino a griglia**, lo stesso
 * oggetto dei giocatori con `destinatari = 'maestri'` (⇒ `listini.ts`,
 * `costoDelCampo`).
 */
//# sourceMappingURL=circolo.js.map